import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import SystemVerilogBanner from "../../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./Systemverilog.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const SystemVerilog = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowThankYou(false);
  const handleSearch = () => setSearchTerm(searchInput.trim());
  const toggleReply = id =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };
  const handleReplySubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };

  const sectionRefs = {
    intro: useRef(null),
    features: useRef(null),
    datatypes: useRef(null),
    oop: useRef(null),
    interfaces: useRef(null),
    assertions: useRef(null),
    random: useRef(null),
    coverage: useRef(null),
    bestPractices: useRef(null),
    ecosystem: useRef(null),
    comments: useRef(null),
  };
  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // scroll to first <mark> on search
  useEffect(() => {
    if (!searchTerm) return;
    const m = document.querySelector("mark");
    m?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade in sections
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        e.isIntersecting
          ? e.target.classList.add("visible")
          : e.target.classList.remove("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss thank-you popup
  useEffect(() => {
    if (showThankYou) {
      const t = setTimeout(() => setShowThankYou(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showThankYou]);

  return (
    <div className="blog-post-page systemverilog">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>SystemVerilog Overview</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | SystemVerilog
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search keyword..."
            className="search-input"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    features: "Key Features",
                    datatypes: "Data Types & Structures",
                    oop: "Object-Oriented SV",
                    interfaces: "Interfaces & Modports",
                    assertions: "Assertions & SVA",
                    random: "Constrained Randomization",
                    coverage: "Functional Coverage",
                    bestPractices: "Best Practices",
                    ecosystem: "Tool & IP Ecosystem",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            src={SystemVerilogBanner}
            alt="SystemVerilog"
            className="post-main-image fade-section"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Alex</span>
            <span className="post-category">Category: ASIC Verification</span>
            <button
              className="comment-link"
              onClick={() => scrollTo(sectionRefs.comments)}
            >
              2 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction to SystemVerilog</h2>
          <p className="fade-section">
            <Highlight
              text="SystemVerilog is an extension of Verilog that adds advanced verification and hardware description capabilities, standardized as IEEE 1800."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="It merges design and verification languages into a unified environment, supporting RTL modeling, assertion-based verification, random testbench generation, and functional coverage."
              query={searchTerm}
            />
          </p>

          {/* 2. Key Features */}
          <h2 ref={sectionRefs.features} className="fade-section">Key Features</h2>
          <ul className="fade-section">
            {[
              "Enhanced data types (logic, bit, byte, enum)",
              "Interfaces and modports for modular connectivity",
              "Object-oriented programming constructs",
              "Assertion constructs (SVA)",
              "Constrained random stimulus",
              "Functional coverage constructs",
            ].map((item,i) => (
              <li key={i}>
                <Highlight text={item} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 3. Data Types & Structures */}
          <h2 ref={sectionRefs.datatypes} className="fade-section">Data Types & Structures</h2>
          <p className="fade-section">
            <Highlight
              text="Beyond reg and wire, SystemVerilog offers logic, bit, byte, int, longint, shortint, and enum types, plus packed/unpacked arrays and structs/unions for rich data modeling."
              query={searchTerm}
            />
          </p>

          {/* 4. Object-Oriented SV */}
          <h2 ref={sectionRefs.oop} className="fade-section">Object-Oriented Programming</h2>
          <p className="fade-section">
            <Highlight
              text="Classes, inheritance, virtual methods, dynamic arrays, mailboxes, and queues enable reusable, modular verification components under UVM or custom frameworks."
              query={searchTerm}
            />
          </p>

          {/* 5. Interfaces & Modports */}
          <h2 ref={sectionRefs.interfaces} className="fade-section">Interfaces & Modports</h2>
          <p className="fade-section">
            <Highlight
              text="Interfaces bundle signals and tasks/functions into a single construct, with modports defining directional views for drivers, monitors, and DUT connections."
              query={searchTerm}
            />
          </p>

          {/* 6. Assertions & SVA */}
          <h2 ref={sectionRefs.assertions} className="fade-section">Assertions & SVA</h2>
          <p className="fade-section">
            <Highlight
              text="SystemVerilog Assertions let you express temporal properties directly in RTL or testbench. SVA supports concurrent and immediate assertions, sequences, and properties."
              query={searchTerm}
            />
          </p>

          {/* 7. Constrained Randomization */}
          <h2 ref={sectionRefs.random} className="fade-section">Constrained Randomization</h2>
          <p className="fade-section">
            <Highlight
              text="With the ‘rand’ qualifier and constraint blocks, you can generate random stimulus under user-defined constraints, ensuring thorough exploration of corner cases."
              query={searchTerm}
            />
          </p>

          {/* 8. Functional Coverage */}
          <h2 ref={sectionRefs.coverage} className="fade-section">Functional Coverage</h2>
          <p className="fade-section">
            <Highlight
              text="Covergroups, coverpoints, and cross coverage allow you to define metrics that track which stimulus scenarios and state combinations have been exercised by your tests."
              query={searchTerm}
            />
          </p>

          {/* 9. Best Practices */}
          <h2 ref={sectionRefs.bestPractices} className="fade-section">Best Practices</h2>
          <ul className="fade-section">
            {[
              "Use ‘logic’ instead of ‘reg’ for single-driver signals",
              "Separate interface definitions from modules",
              "Encapsulate sequence logic in classes",
              "Keep assertions and coverage in verification layers",
              "Leverage named constraints and random stability",
            ].map((bp,i) => (
              <li key={i}>
                <Highlight text={bp} query={searchTerm} />
              </li>
            ))}
          </ul>

          {/* 10. Tool & IP Ecosystem */}
          <h2 ref={sectionRefs.ecosystem} className="fade-section">Tool & IP Ecosystem</h2>
          <p className="fade-section">
            <Highlight
              text="All major simulators (VCS, Questa, Xcelium) support SystemVerilog. UVM libraries, vendor IP blocks, and open-source projects like OVM and SVUnit enrich the ecosystem."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">SV Expert</span>
                    <span className="comment-date">May 21, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great overview of SVA—really clarified sequences vs. properties!
              </p>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Rand Guru</span>
                    <span className="comment-date">May 22, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                The constrained random examples are spot on—thanks for sharing!
              </p>
              <button className="reply-button" onClick={() => toggleReply("randguru")}>Reply</button>
              {replyVisible["randguru"] && (
                <div className="leave-comment">
                  <h3>Reply to Rand Guru</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">Reply</button>
                  </form>
                </div>
              )}
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search keyword..."
              className="search-input"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <button onClick={handleSearch} className="search-button">Search</button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      features: "Key Features",
                      datatypes: "Data Types & Structures",
                      oop: "Object-Oriented SV",
                      interfaces: "Interfaces & Modports",
                      assertions: "Assertions & SVA",
                      random: "Constrained Randomization",
                      coverage: "Functional Coverage",
                      bestPractices: "Best Practices",
                      ecosystem: "Tool & IP Ecosystem",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showThankYou && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SystemVerilog;